import { AuthErrorCode } from '@today/types'
import axios, { type AxiosError, type InternalAxiosRequestConfig, isAxiosError } from 'axios'
import { FORCED_MFA_ENROLMENT_PATH } from '@/auth/forced-mfa-enrolment'

export const apiClient = axios.create({
	baseURL: import.meta.env.VITE_API_URL || '/api',
	headers: {
		'Content-Type': 'application/json',
	},
	// Send the HttpOnly refresh cookie on /auth/refresh + /auth/logout (and let
	// the server set it on /auth/callback). The API's CORS allowCredentials is
	// enabled in tandem (infra/common/api.ts).
	withCredentials: true,
})

// Request interceptor to add the access token. It lives in localStorage so the
// login persists across tab close and browser restart. The REFRESH token is
// never here — it's an HttpOnly cookie the browser attaches automatically.
apiClient.interceptors.request.use(
	(config) => {
		const token = localStorage.getItem('accessToken')
		if (token) {
			config.headers.Authorization = `Bearer ${token}`
		}
		return config
	},
	(error) => Promise.reject(error),
)

// Retried requests carry a flag so a second 401 doesn't loop.
type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean }

/** A 401 on a non-auth request we haven't already retried is refresh-eligible. */
function canAttemptRefresh(
	status: number | undefined,
	original: RetriableConfig | undefined,
): original is RetriableConfig {
	if (status !== 401 || !original || original._retried) return false
	const url = original.url ?? ''
	return !url.includes('/auth/refresh') && !url.includes('/auth/logout')
}

// Response interceptor: handle account-inactive, then attempt a silent refresh
// on a 401 (once) by delegating to authService.refresh() — the refresh logic +
// single-flight dedup live in the auth service. Lazy import avoids the
// api.client ↔ auth.service circular dependency. Falls back to /login.
apiClient.interceptors.response.use(
	(response) => response,
	async (error: unknown) => {
		if (!isAxiosError(error)) return Promise.reject(error)
		const axiosError = error as AxiosError<{ error?: { code?: string } }>
		const status = axiosError.response?.status
		const original = axiosError.config as RetriableConfig | undefined

		if (axiosError.response?.data?.error?.code === AuthErrorCode.ACCOUNT_INACTIVE) {
			localStorage.removeItem('accessToken')
			window.location.href = '/error/inactive'
			return Promise.reject(error)
		}

		// The force-enrolment gate refuses every non-enrolment surface for a
		// required-but-unenrolled user (TO-591 REQ-109). Honour it client-side by
		// sending them to the one flow that lifts the block. The enrolment routes
		// set `allowMfaUnenrolled` server-side, so this code never comes from them —
		// no redirect loop. The session stays valid (no token clear): enrolment
		// needs it; the grant revokes it afterwards, handled as a 401 then.
		if (axiosError.response?.data?.error?.code === AuthErrorCode.MFA_ENROLMENT_REQUIRED) {
			window.location.href = FORCED_MFA_ENROLMENT_PATH
			return Promise.reject(error)
		}

		if (canAttemptRefresh(status, original)) {
			original._retried = true
			const { authService } = await import('./auth.service')
			const newToken = await authService.refresh()
			if (newToken) {
				original.headers.Authorization = `Bearer ${newToken}`
				return apiClient(original)
			}
		}

		if (status === 401) {
			localStorage.removeItem('accessToken')
			window.location.href = '/login'
		}
		return Promise.reject(error)
	},
)
